import React, { useState, useEffect } from 'react'
import { processTemplate, extractVariables } from '../../utils/configVariables'

const ConfigValueInput = ({ value, onChange, configs = [], error }) => {
  const [preview, setPreview] = useState('')
  const [variables, setVariables] = useState([])
  const [previewError, setPreviewError] = useState('')

  useEffect(() => {
    const vars = extractVariables(value)
    setVariables(vars)

    if (vars.length > 0) {
      const result = processTemplate(value, configs)
      setPreview(result.processed)
      setPreviewError(result.errors.length > 0 ? result.errors[0] : '')
    } else {
      setPreview(value)
      setPreviewError('')
    }
  }, [value, configs])

  const renderValueWithHighlight = () => {
    if (!value) return null

    const parts = []
    const regex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g
    let lastIndex = 0
    let match

    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: value.substring(lastIndex, match.index),
        })
      }

      const varName = match[1]
      const varExists = configs.some(c => c.key === varName)

      parts.push({
        type: 'variable',
        content: `{{${varName}}}`,
        varName,
        exists: varExists,
      })

      lastIndex = regex.lastIndex
    }

    if (lastIndex < value.length) {
      parts.push({
        type: 'text',
        content: value.substring(lastIndex),
      })
    }

    return parts.map((part, idx) => {
      if (part.type === 'text') {
        return <span key={idx}>{part.content}</span>
      }
      return (
        <span
          key={idx}
          className={`px-1.5 py-0.5 rounded font-mono text-xs ${
            part.exists
              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
              : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700'
          }`}
        >
          {part.content}
        </span>
      )
    })
  }

  return (
    <div className="space-y-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ex: https://api.exemplo.com ou {{url_base}}/busca"
        rows={3}
        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none ${
          error
            ? 'border-red-500 dark:border-red-500'
            : 'border-gray-300 dark:border-gray-600'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />

      {variables.length > 0 && (
        <div className="space-y-2">
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Visualização com valores:
            </p>
            <div className="bg-white dark:bg-gray-800 rounded p-2 text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
              {previewError ? (
                <span className="text-red-600 dark:text-red-400">
                  {previewError}
                </span>
              ) : (
                preview
              )}
            </div>
          </div>

          {variables.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2">
              <p className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-1">
                Variáveis usadas:
              </p>
              <div className="flex flex-wrap gap-1">
                {variables.map((varName) => {
                  const varExists = configs.some(c => c.key === varName)
                  return (
                    <span
                      key={varName}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-mono ${
                        varExists
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                          : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                      }`}
                    >
                      {`{{${varName}}}`}
                      {!varExists && (
                        <span className="ml-1" title="Não encontrada">
                          ⚠️
                        </span>
                      )}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-red-500 dark:text-red-400 text-xs">{error}</p>
      )}
    </div>
  )
}

export default ConfigValueInput
